/* eslint-disable */
import { useState, useEffect, useCallback } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import Toast, { useToast } from "./Toast";
import { COLOR, FONT, RADIUS } from "../utils/theme";
import { Icon } from "../utils/icons";
import useWindowWidth from "../utils/useWindowWidth";
import { takeSnapshot } from "../utils/snapshots";

const STYLE_OPTIONS = ["City life", "Adventure", "Foodie", "Culture", "Nature", "Relaxation", "Nightlife"];
const BUDGET_OPTIONS = ["Budget", "Mid-range", "Premium", "Luxury"];

const labelStyle = {
  fontSize: 11, color: COLOR.inkMid, fontFamily: FONT.mono,
  letterSpacing: "0.5px", textTransform: "uppercase",
  marginBottom: 6, display: "block",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: COLOR.white, border: `1px solid ${COLOR.borderWarm}`,
  borderRadius: RADIUS.sm, padding: "10px 12px",
  color: COLOR.inkDark, fontSize: 15,
  fontFamily: FONT.serif,
  outline: "none", transition: "border-color 0.15s",
};
const focus = e => e.target.style.borderColor = COLOR.mauve;
const blur  = e => e.target.style.borderColor = COLOR.borderWarm;

const LS_KEY = "wandero_tripeditpanel_width";

export default function TripEditPanel({ tripData, slug, days, onClose, onSave }) {
  const [closing, setClosing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, showToast] = useToast();

  const { width, isDesktop } = useWindowWidth();

  // ── Desktop resize state ────────────────────────────────────────
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? Number(saved) : null;
  });

  useEffect(() => {
    if (panelWidth === null) {
      localStorage.removeItem(LS_KEY);
    } else {
      localStorage.setItem(LS_KEY, panelWidth);
    }
  }, [panelWidth]);

  const resolvedWidth = isDesktop
    ? (panelWidth ?? (width - 80))
    : "100%";

  const handleResizeDragStart = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth ?? (width - 80);

    const onMove = (ev) => {
      const delta = startX - ev.clientX;
      const newWidth = Math.min(
        width - 80,
        Math.max(width * 0.40, startWidth + delta)
      );
      setPanelWidth(newWidth);
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleResizeDoubleClick = () => setPanelWidth(null);

  // ── Form state ──────────────────────────────────────────────────
  const brief = tripData?.brief || {};

  const toDisplayPref = (v) => v === true ? "show" : v === false ? "hide" : "default";

  const initForm = useCallback((td) => {
    const b = td?.brief || {};
    return {
      title:         td?.title || "",
      departureCity: b.departureCity || "",
      styles:        Array.isArray(b.style) ? [...b.style] : [],
      budget:        b.budget || "",
      mustSees:      Array.isArray(b.mustSees) ? b.mustSees.join(", ") : (b.mustSees || ""),
      avoid:         Array.isArray(b.avoid) ? b.avoid.join(", ") : (b.avoid || ""),
      travelers:     (() => {
        if (Array.isArray(b.travelerNames) && b.travelerNames.length > 0) {
          return b.travelerNames.join(', ');
        }
        if (Array.isArray(b.travelers)) {
          return b.travelers.join(', ');
        }
        if (typeof b.travelers === 'string') return b.travelers;
        return '';
      })(),
      showBookingInfo:  toDisplayPref(td?.showBookingInfo),
      showCostEstimates: toDisplayPref(td?.showCostEstimates),
      showLocation:     toDisplayPref(td?.showLocation),
    };
  }, []);

  const [form, setForm] = useState(() => initForm(tripData));
  const [initial, setInitial] = useState(() => initForm(tripData));

  useEffect(() => {
    const next = initForm(tripData);
    setForm(next);
    setInitial(next);
  }, [tripData?.title, initForm]); // eslint-disable-line

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  const toggleStyle = (s) => {
    setForm(f => ({
      ...f,
      styles: f.styles.includes(s) ? f.styles.filter(x => x !== s) : [...f.styles, s],
    }));
  };

  const isDirty =
    form.title !== initial.title ||
    form.departureCity !== initial.departureCity ||
    JSON.stringify(form.styles) !== JSON.stringify(initial.styles) ||
    form.budget !== initial.budget ||
    form.mustSees !== initial.mustSees ||
    form.avoid !== initial.avoid ||
    form.travelers !== initial.travelers ||
    form.showBookingInfo !== initial.showBookingInfo ||
    form.showCostEstimates !== initial.showCostEstimates ||
    form.showLocation !== initial.showLocation;

  // ── Dismiss ─────────────────────────────────────────────────────
  const dismiss = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 260);
  }, [onClose]);

  // ── Save ────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (saving || !isDirty) return;
    setSaving(true);
    try {
      const updates = {};

      if (form.title !== initial.title) {
        updates.title = form.title.trim() || initial.title;
      }
      if (form.departureCity !== initial.departureCity) {
        updates["brief.departureCity"] = form.departureCity.trim();
      }
      if (JSON.stringify(form.styles) !== JSON.stringify(initial.styles)) {
        updates["brief.style"] = form.styles;
      }
      if (form.budget !== initial.budget) {
        updates["brief.budget"] = form.budget;
      }
      if (form.mustSees !== initial.mustSees) {
        updates["brief.mustSees"] = form.mustSees.trim()
          ? form.mustSees.split(",").map(s => s.trim()).filter(Boolean)
          : [];
      }
      if (form.avoid !== initial.avoid) {
        updates["brief.avoid"] = form.avoid.trim()
          ? form.avoid.split(",").map(s => s.trim()).filter(Boolean)
          : [];
      }
      if (form.travelers !== initial.travelers) {
        const namesArray = form.travelers.trim()
          ? form.travelers.split(",").map(s => s.trim()).filter(Boolean)
          : [];
        updates["brief.travelerNames"] = namesArray;
        updates["brief.travelers"] = namesArray.length || tripData?.brief?.travelers || 0;
      }

      const fromDisplayPref = (v) => v === "show" ? true : v === "hide" ? false : null;
      if (form.showBookingInfo !== initial.showBookingInfo) {
        updates.showBookingInfo = fromDisplayPref(form.showBookingInfo);
      }
      if (form.showCostEstimates !== initial.showCostEstimates) {
        updates.showCostEstimates = fromDisplayPref(form.showCostEstimates);
      }
      if (form.showLocation !== initial.showLocation) {
        updates.showLocation = fromDisplayPref(form.showLocation);
      }

      if (Object.keys(updates).length === 0) return;

      takeSnapshot(db, slug, "Trip preferences edited", days, []).catch(err =>
        console.error("Snapshot failed:", err)
      );
      await updateDoc(doc(db, "trips", slug), updates);
      onSave?.();
      showToast("Trip preferences updated", "success");
      setTimeout(() => { setClosing(true); setTimeout(onClose, 260); }, 600);
    } catch (e) {
      setSaving(false);
      showToast("Save failed — please try again", "error");
    }
  };

  return (
    <>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />
      <div
        onClick={dismiss}
        style={{
          position: "fixed", inset: 0, zIndex: 300,
          display: "flex",
          alignItems: "stretch",
          justifyContent: isDesktop ? "flex-end" : "center",
          flexDirection: isDesktop ? "row" : "column",
          background: closing ? `${COLOR.inkDark}00` : `${COLOR.inkDark}80`,
          transition: "background 0.26s",
        }}
      >
        <div
          style={{ position: "relative", display: "contents" }}
          onClick={e => e.stopPropagation()}
        >
          <div
            data-testid="trip-edit-panel"
            style={{
              position: "relative",
              background: COLOR.blush,
              borderRadius: isDesktop ? 0 : "24px 24px 0 0",
              width: isDesktop ? resolvedWidth : "100%",
              maxWidth: isDesktop ? resolvedWidth : "100%",
              height: isDesktop ? "100vh" : undefined,
              maxHeight: isDesktop ? "100vh" : "100dvh",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              paddingBottom: isDesktop ? 24 : "max(16px, env(safe-area-inset-bottom))",
              animation: `${closing ? (isDesktop ? "slideOutRight" : "slideDown") : (isDesktop ? "slideInRight" : "slideUp")} 0.26s cubic-bezier(0.32,0.72,0,1) forwards`,
              boxShadow: isDesktop ? `0 0 60px ${COLOR.inkDark}40` : "none",
            }}
          >
            {/* Desktop resize handle */}
            {isDesktop && (
              <div
                onMouseDown={handleResizeDragStart}
                onDoubleClick={handleResizeDoubleClick}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 6,
                  cursor: "ew-resize",
                  background: "transparent",
                  zIndex: 10,
                }}
              />
            )}
            {/* Mobile drag handle */}
            {!isDesktop && (
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: COLOR.borderWarm }} />
              </div>
            )}

            {/* Header */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: isDesktop ? "18px 20px 14px" : "10px 20px 14px",
              borderBottom: `1px solid ${COLOR.borderWarm}`,
            }}>
              <div style={{ fontSize: 20, fontWeight: 600, color: COLOR.inkDark, fontFamily: FONT.serif }}>
                Edit Trip
              </div>
              <button
                data-testid="trip-edit-close"
                onClick={dismiss}
                style={{
                  background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
                  borderRadius: 10, width: 40, height: 40, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}
              >
                <Icon name="close" size={16} color={COLOR.inkMid} />
              </button>
            </div>

            {/* Helper text */}
            <div style={{
              padding: "14px 20px 0",
              fontSize: 12, fontFamily: FONT.mono, color: COLOR.inkMid,
              marginBottom: 16,
            }}>
              These preferences guide AI when generating or rewriting activities for this trip.
            </div>

            {/* Form */}
            <div style={{ padding: "0 20px" }}>

              {/* Section header */}
              <div style={{
                fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono,
                letterSpacing: "0.12em", textTransform: "uppercase",
                marginBottom: 16, paddingTop: 4,
              }}>
                TRIP PREFERENCES
              </div>

              {/* Title */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>TRIP TITLE</label>
                <input
                  value={form.title}
                  onChange={set("title")}
                  onFocus={focus} onBlur={blur}
                  placeholder="e.g. Scandinavia Summer 2026"
                  style={inputStyle}
                />
              </div>

              {/* Departure City */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>DEPARTURE CITY</label>
                <input
                  value={form.departureCity}
                  onChange={set("departureCity")}
                  onFocus={focus} onBlur={blur}
                  placeholder="e.g. Chicago"
                  style={inputStyle}
                />
              </div>

              {/* Travel Styles — multi-select pills */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>TRAVEL STYLES</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingTop: 4 }}>
                  {STYLE_OPTIONS.map(s => (
                    <button
                      key={s}
                      onClick={() => toggleStyle(s)}
                      style={{
                        padding: "6px 14px",
                        borderRadius: RADIUS.pill,
                        border: `1px solid ${form.styles.includes(s) ? COLOR.mauve : COLOR.borderWarm}`,
                        background: form.styles.includes(s) ? COLOR.blush : COLOR.white,
                        color: form.styles.includes(s) ? COLOR.mauveDark : COLOR.inkMid,
                        fontFamily: FONT.serif, fontSize: 13,
                        cursor: "pointer", transition: "all 0.15s",
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget — single-select pills */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>BUDGET</label>
                <div style={{ display: "flex", gap: 0, paddingTop: 4, borderRadius: RADIUS.md, overflow: "hidden", border: `1px solid ${COLOR.borderWarm}` }}>
                  {BUDGET_OPTIONS.map((b, i) => (
                    <button
                      key={b}
                      onClick={() => setForm(f => ({ ...f, budget: f.budget === b ? "" : b }))}
                      style={{
                        flex: 1, padding: "8px 4px",
                        background: form.budget === b ? COLOR.mauve : COLOR.white,
                        color: form.budget === b ? COLOR.white : COLOR.inkMid,
                        border: "none",
                        borderRight: i < BUDGET_OPTIONS.length - 1 ? `1px solid ${COLOR.borderWarm}` : "none",
                        fontFamily: FONT.mono, fontSize: 10,
                        letterSpacing: "0.05em", textTransform: "uppercase",
                        cursor: "pointer", transition: "all 0.15s",
                      }}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>

              {/* Must-sees */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>MUST-SEES</label>
                <input
                  value={form.mustSees}
                  onChange={set("mustSees")}
                  onFocus={focus} onBlur={blur}
                  placeholder="e.g. Great Barrier Reef, Sydney Opera House"
                  style={inputStyle}
                />
              </div>

              {/* Avoid */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>AVOID</label>
                <input
                  value={form.avoid}
                  onChange={set("avoid")}
                  onFocus={focus} onBlur={blur}
                  placeholder="e.g. long bus rides, tourist traps"
                  style={inputStyle}
                />
              </div>

              {/* Travelers */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>TRAVELERS</label>
                <input
                  data-testid="trip-edit-travelers"
                  value={form.travelers}
                  onChange={set("travelers")}
                  onFocus={focus} onBlur={blur}
                  placeholder="e.g. Alice, Bob"
                  style={inputStyle}
                />
              </div>

            </div>

            {/* Display Settings */}
            <div style={{ padding: "0 20px", marginTop: 8 }}>
              <div style={{
                fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono,
                letterSpacing: "0.12em", textTransform: "uppercase",
                marginBottom: 8, paddingTop: 4,
              }}>
                DISPLAY SETTINGS
              </div>
              <div style={{
                fontSize: 11, fontFamily: FONT.mono, color: COLOR.inkLight,
                marginBottom: 16,
              }}>
                Override your global settings for this trip.
              </div>

              {[
                { key: "showBookingInfo", label: "BOOKING INFO" },
                { key: "showCostEstimates", label: "COST ESTIMATES" },
                { key: "showLocation", label: "LOCATION" },
              ].map(({ key, label }) => (
                <div key={key} style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>{label}</label>
                  <select
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    onFocus={focus} onBlur={blur}
                    style={{ ...inputStyle, appearance: "auto" }}
                  >
                    <option value="default">Use default</option>
                    <option value="show">Show</option>
                    <option value="hide">Hide</option>
                  </select>
                </div>
              ))}
            </div>

            {/* Save button */}
            <div style={{
              padding: "12px 20px",
              display: "flex",
              justifyContent: isDesktop ? "flex-end" : "stretch",
            }}>
              <button
                data-testid="trip-edit-save"
                onClick={handleSave}
                disabled={!isDirty || saving}
                style={{
                  background: isDirty && !saving ? COLOR.mauve : `${COLOR.mauve}66`,
                  color: COLOR.white,
                  border: "none", borderRadius: RADIUS.md,
                  fontFamily: FONT.mono, fontSize: 13,
                  textTransform: "uppercase", letterSpacing: "0.5px",
                  padding: "14px 28px", minHeight: 48,
                  cursor: isDirty && !saving ? "pointer" : "default",
                  transition: "background 0.15s",
                  width: isDesktop ? "auto" : "100%",
                }}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
